import React, { useState, useEffect } from "react";
import { ReferenceForm, ReferenceFormData } from "./ReferenceForm";
import { ReferenceCard } from "./ReferenceCard";
import { Button } from "@components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/shadcn-ui/dialog";

interface ReferenceManagerProps {
  applicationId: string;
  leadId: string;
  leadPhoneNumber: string;
  initialReferences?: ReferenceFormData[];
  onReferencesUpdated?: (references: ReferenceFormData[]) => void;
  readOnly?: boolean;
}

export const ReferenceManager: React.FC<ReferenceManagerProps> = ({
  applicationId,
  leadId,
  leadPhoneNumber,
  initialReferences = [],
  onReferencesUpdated,
  readOnly = false,
}) => {
  const [references, setReferences] = useState<ReferenceFormData[]>(initialReferences);
  const [isLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
  const { toast } = useToast();

  // Update references when initialReferences changes (e.g., when loaded from API)
  useEffect(() => {
    if (initialReferences && initialReferences.length > 0) {
      console.log("Setting references from initialReferences:", initialReferences);
      setReferences(initialReferences);
    }
  }, [initialReferences]);

  // Effect to notify parent component when references change
  useEffect(() => {
    if (onReferencesUpdated) {
      onReferencesUpdated(references);
    }
  }, [references, onReferencesUpdated]);

  const handleReferenceAdded = (newReference: ReferenceFormData) => {
    setReferences(prev => [...prev, newReference]);
    setShowAddForm(false);
    toast({
      description: "Reference added successfully",
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteReference = (index: number) => {
    if (window.confirm("Are you sure you want to delete this reference?")) {
      const updatedReferences = [...references];
      updatedReferences.splice(index, 1);
      setReferences(updatedReferences);
      toast({
        description: "Reference deleted successfully",
      });
    }
  };

  const handleEditReference = (index: number) => {
    setCurrentEditIndex(index);
    setIsEditDialogOpen(true);
  };

  const handleUpdateReference = (updatedReference: ReferenceFormData) => {
    if (currentEditIndex === null) return;
    
    const updatedReferences = [...references];
    updatedReferences[currentEditIndex] = updatedReference;
    setReferences(updatedReferences);
    setIsEditDialogOpen(false);
    setCurrentEditIndex(null);
    toast({
      description: "Reference updated successfully",
    });
  };

  // Get all existing phone numbers except the one being edited
  const getExistingPhoneNumbers = (excludeIndex?: number) => {
    return references
      .filter((_, index) => index !== excludeIndex)
      .map(ref => ref.personalDetails.phoneNumber);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading references...</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 mt-4">
        <h2 className="text-lg font-semibold">Reference Details</h2>
        {!readOnly && (
          <Button 
            onClick={() => setShowAddForm(true)}
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> Add Reference
          </Button>
        )}
      </div>

      {showAddForm && (
        <ReferenceForm
          applicationId={applicationId}
          leadId={leadId}
          onReferenceAdded={handleReferenceAdded}
          onCancel={() => setShowAddForm(false)}
          existingPhoneNumbers={getExistingPhoneNumbers()}
          leadPhoneNumber={leadPhoneNumber}
        />
      )}

      {references.length === 0 && !showAddForm ? (
        <div className="text-center p-8 border border-dashed rounded-md">
          <p className="text-gray-500">No references added yet</p>
          {!readOnly && (
            <Button 
              onClick={() => setShowAddForm(true)}
              variant="outline"
              className="mt-2"
            >
              Add your first reference
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {references.map((reference, index) => (
            <ReferenceCard
              key={index}
              reference={reference}
              index={index}
              onEdit={() => !readOnly && handleEditReference(index)}
              applicationId={applicationId}
            />
          ))}
        </div>
      )}

      {/* Edit Reference Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
            <DialogTitle>Edit Reference</DialogTitle>
          </DialogHeader>
          {currentEditIndex !== null && (
            <ReferenceForm
              applicationId={applicationId}
              leadId={leadId}
              onReferenceAdded={handleUpdateReference}
              onCancel={() => setIsEditDialogOpen(false)}
              initialData={references[currentEditIndex]}
              isEditMode={true}
              existingPhoneNumbers={getExistingPhoneNumbers(currentEditIndex)}
              leadPhoneNumber={leadPhoneNumber}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
